// Service pour charger et gérer les données d'analyse des podcasts
import { MetadataService } from './MetadataService.js'
import { getBestScoredContent } from '../utils/scoreUtils.js'

export class DataService {
  static episodesCache = null

  static async loadAllEpisodes() {
    try {
      // Charger la liste des fichiers depuis l'index généré automatiquement
      const episodeFiles = await this.loadFilesIndex()

      const episodes = await Promise.all(
        episodeFiles.map(async (filename) => {
          try {
            // Charger depuis le dossier public
            const response = await fetch(`${import.meta.env.BASE_URL}data/analysis/${filename}`)
            if (!response.ok) {
              console.warn(`Fichier non trouvé: ${filename} (${response.status})`)
              return null
            }
            const data = await response.json()
            
            // Normaliser les données pour assurer la cohérence
            return this.normalizeEpisodeData(data, filename)
          } catch (error) {
            console.warn(`Impossible de charger ${filename}:`, error.message)
            return null
          }
        })
      )

      // Filtrer les épisodes qui n'ont pas pu être chargés et trier par date
      const validEpisodes = episodes
        .filter(episode => episode !== null)
        .sort((a, b) => new Date(b.analysisDate) - new Date(a.analysisDate))

      if (validEpisodes.length > 0) {
        console.log(`✅ Chargé ${validEpisodes.length} épisodes sur ${episodeFiles.length} tentatives`)
      } else {
        console.warn(`⚠️ Aucun épisode chargé sur ${episodeFiles.length} tentatives`)
      }
      
      // Mettre en cache les épisodes
      this.episodesCache = validEpisodes
      return validEpisodes
    } catch (error) {
      console.error('Erreur lors du chargement des épisodes:', error)
      throw error
    }
  }

  // Méthode getAllEpisodes qui utilise le cache ou charge les données
  static async getAllEpisodes() {
    if (this.episodesCache) {
      return this.episodesCache
    }
    return await this.loadAllEpisodes()
  }

  // Obtenir un épisode par ID
  static async getEpisodeById(episodeId) {
    const episodes = await this.getAllEpisodes()
    return episodes.find(episode => episode.id === episodeId) || null
  }

  static normalizeEpisodeData(data, filename) {
    // Extraire l'ID de l'épisode depuis le nom de fichier
    const episodeId = filename.replace('_analysis.json', '')
    
    // Normaliser la structure des données
    const normalized = {
      id: data.episode_id || episodeId,
      title: data.episode_title || this.formatTitle(episodeId),
      summary: data.summary || '',
      date: data.date || null,
      duration: data.duration || null,
      analysisDate: data.analysis_date || new Date().toISOString(),
      transcriptLength: data.transcript_length || 0,
      modelUsed: data.model_used || 'unknown',
      isPromoEpisode: data.is_promo_episode || false,
      
      // Normaliser les actualités
      introNews: this.normalizeContentArray(data.intro_news),
      
      // Normaliser les jeux
      games: this.normalizeContentArray(data.games),
      
      // Recommandations culturelles (garder la structure originale avec type et title)
      cultureRecommendations: data.culture_recommendations || [],
      
      // Normaliser les moments notables
      notableMoments: this.normalizeContentArray(data.notable_moments),
      
      // Normaliser les points de discussion
      discussionPoints: this.normalizeContentArray(data.discussion_points),
      
      // Données simples
      guests: data.guests || [],
      participants: data.participants || [],
      themes: data.themes || [],
      podcast: data.podcast || null,
      
      // Données spécifiques à Podchiasse
      annoyance_stories: data.annoyance_stories || [],
      farfie_moments: data.farfie_moments || [],
      notable_moments: data.notable_moments || [],
      
      // Meilleurs contenus avec scores
      bestScoredContent: getBestScoredContent(data),
      
      // Données brutes pour la recherche
      rawData: data
    }

    return normalized
  }

  static normalizeContentArray(content) {
    if (!content) return []
    
    return content.map(item => {
      if (typeof item === 'string') {
        return { content: item, timestamp: null }
      } else if (typeof item === 'object' && item.content) {
        return {
          content: item.content,
          timestamp: item.timestamp || null
        }
      }
      return { content: String(item), timestamp: null }
    })
  }

  // Charger la liste des fichiers depuis l'index
  static async loadFilesIndex() {
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}data/analysis/index.json`)
      if (!response.ok) {
        console.warn('Index des fichiers non trouvé, utilisation de la liste par défaut')
        return this.getDefaultFilesList()
      }
      
      const index = await response.json()
      console.log(`📋 Index chargé: ${index.totalFiles} fichiers disponibles`)
      return index.files || []
    } catch (error) {
      console.warn('Erreur lors du chargement de l\'index, utilisation de la liste par défaut:', error)
      return this.getDefaultFilesList()
    }
  }

  // Liste par défaut en cas d'échec de chargement de l'index
  static getDefaultFilesList() {
    return [
      's10e28_victor_lanoux_prime_analysis.json',
      's10e29_addict_au_confort_analysis.json',
      's10e30_la_sismance_analysis.json',
      's10e31_fc_taxi_2_analysis.json',
      's10e32_les_annes_golden_moustache_analysis.json',
      's10e33_fossile_de_shirley_et_dino_analysis.json',
      's10e34_fabien_au_lit_tard_analysis.json',
      's10e35_farfelue_de_terrain_analysis.json',
      's10e36_le_cosy_floodcast_analysis.json',
      's10e37_la_pouproule_analysis.json',
      's10e38_bonnes_blagues_dia_analysis.json',
      's10e39_ctait_un_rve_raliser_analysis.json',
      's10e40_le_cri_du_jugnot_analysis.json',
      'il_sagissait_du_floodcast_analysis.json'
    ]
  }

  static formatTitle(episodeId) {
    // Convertir l'ID en titre lisible
    return episodeId
      .replace(/^s\d+e\d+_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }


  // Obtenir l'URL audio depuis les métadonnées
  static getAudioUrl(episodeId, timestamp = null) {
    // Essayer d'abord depuis les métadonnées
    const audioUrl = MetadataService.getAudioUrl(episodeId)
    
    if (audioUrl) {
      let finalUrl = audioUrl
      
      // Si on a un timestamp, on peut l'ajouter en fragment (pas tous les lecteurs le supportent)
      if (timestamp) {
        const timeInSeconds = this.timestampToSeconds(timestamp)
        finalUrl += `#t=${timeInSeconds}`
      }
      
      return finalUrl
    }
    
    // Fallback vers l'ancien système si pas d'URL trouvée dans les métadonnées
    if (this.episodesCache) {
      const episode = this.episodesCache.find(ep => ep.id === episodeId)
      if (episode && episode.rawData && episode.rawData.url) {
        let fallbackUrl = episode.rawData.url
        
        if (timestamp) {
          const timeInSeconds = this.timestampToSeconds(timestamp)
          fallbackUrl += `#t=${timeInSeconds}`
        }
        
        return fallbackUrl
      }
    }
    
    // Aucune URL trouvée
    return null
  }

  // Générer des URLs Spotify (à adapter selon les vrais liens)
  static getSpotifyUrl(episodeId, timestamp = null) {
    // Pour le moment, générer une URL factice pour que le widget fonctionne
    const baseUrl = `https://open.spotify.com/episode/`
    const spotifyEpisodeId = this.mapToSpotifyId(episodeId)
    
    // Si on n'a pas de vrai ID, utiliser un ID factice pour tester
    const episodeIdToUse = spotifyEpisodeId || 'demo-episode-id'
    
    if (timestamp) {
      // Spotify supporte les timestamps avec le paramètre t
      const timeInSeconds = this.timestampToSeconds(timestamp)
      return `${baseUrl}${episodeIdToUse}?t=${timeInSeconds}`
    }
    
    return `${baseUrl}${episodeIdToUse}`
  }

  static mapToSpotifyId(episodeId) {
    // Mapping des vrais IDs Spotify du Floodcast
    // En attendant les vrais IDs, on en simule quelques-uns
    const spotifyMapping = {
      's10e40_le_cri_du_jugnot': '4rOoJ6Egrf8K2IrywzwOMk',
      's10e39_ctait_un_rve_raliser': '1A4chjlR6JkCYFhR4k6bN7',
      's10e38_bonnes_blagues_dia': 'demo-episode-38',
      's10e37_la_pouproule': 'demo-episode-37',
      's10e36_le_cosy_floodcast': 'demo-episode-36',
      // Ajouter d'autres mappings factices pour tester
    }
    
    return spotifyMapping[episodeId] || `demo-${episodeId}`
  }

  static timestampToSeconds(timestamp) {
    if (!timestamp) return 0
    
    // Convertir des formats comme "25:12", "106:07" ou "1:05:30" en secondes
    const parts = timestamp.split(':').map(Number)
    
    if (parts.length === 2) {
      // Format MM:SS
      const minutes = parts[0]
      const seconds = parts[1]
      
      // Si minutes >= 60, c'est probablement HH:MM en réalité
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60)
        const remainingMinutes = minutes % 60
        return hours * 3600 + remainingMinutes * 60 + seconds
      } else {
        // Format normal MM:SS
        return minutes * 60 + seconds
      }
    } else if (parts.length === 3) {
      // Format HH:MM:SS
      return parts[0] * 3600 + parts[1] * 60 + parts[2]
    }
    
    return 0
  }

  // Méthode utilitaire pour le debug
  static logDataStructure(episodes) {
    if (episodes.length === 0) return

    console.group('📊 Structure des données chargées')
    console.log(`Nombre d'épisodes: ${episodes.length}`)
    
    const sampleEpisode = episodes[0]
    console.log('Structure d\'un épisode:', {
      id: sampleEpisode.id,
      title: sampleEpisode.title,
      guests: sampleEpisode.guests.length,
      games: sampleEpisode.games.length,
      news: sampleEpisode.introNews.length,
      themes: sampleEpisode.themes.length
    })

    // Statistiques globales
    const stats = {
      totalGuests: new Set(episodes.flatMap(ep => ep.guests)).size,
      totalGames: episodes.reduce((sum, ep) => sum + ep.games.length, 0),
      totalNews: episodes.reduce((sum, ep) => sum + ep.introNews.length, 0),
      episodesWithTimestamps: episodes.filter(ep => 
        ep.games.some(g => g.timestamp) || ep.introNews.some(n => n.timestamp)
      ).length
    }
    
    console.log('Statistiques:', stats)
    console.groupEnd()
  }
} 